/* !=====================================================================
 %%% File Name    : ajaxc.js
 %%% Authors      : Anders Hjelm, anders.hjelm@teliasonera.com
 %%%
 %%% Created      : 2016-02-10
 %%% Copyright    : TeliaSonera Sverige AB 2016
 %%%                Box 1218
 %%%                SE-751 42 Uppsala, Sweden
 %%%                The program may be used and/or copied only with the written
 %%%                permission from TeliaSonera Sverige AB, or in accordance with the
 %%%                terms and conditions stipulated in the agreement/contract
 %%%                under which the program has been supplied.
 %%%                All rights reserved.
 %%%
 %%% Purpose      : Module for ajax communication, with CORS
 ========================================================================== */

import Q from 'q';
import { createLogger } from '@telia-ace/edge-log';

const log = createLogger('utils/ajaxc');

// Separate function that adds encoding of '!' to what is encoded
// in encodeURIComponent.
const encodeURIExtended = (str) => encodeURIComponent(str).replace(/!/g, '%21');

// In IE8 and IE9 XDomainRequest has to be used. For other browsers,
// XMLHttpRequest is used. This function returns IE version. Hopefully.
// If not IE, version is 99.
const getInternetExplorerVersion = () => {
  // Returns the version of Internet Explorer or 99
  // (indicating the use of another browser).

  let rv = 99; // Return value assumes failure.
  if (navigator.appName === 'Microsoft Internet Explorer') {
    const ua = navigator.userAgent;
    const re = new RegExp('MSIE ([0-9]{1,}[.0-9]{0,})');
    if (re.exec(ua) !== null) {
      rv = parseFloat(RegExp.$1);
    }
  }
  return rv;
};

const ieVersion = getInternetExplorerVersion();
const defTimeout = 30000; // ms, default value

export function callWS(url, data, timeout) {
  return ajaxCall(url, data, timeout, 'POST', 'json');
}

export function sendAjaxRestRequest(url, data, timeout, method) {
  return ajaxCall(url, data, timeout, method, 'json', true);
}

export function getTextFile(url, timeout) {
  return ajaxCall(url, {}, timeout, 'GET', '');
}

function ajaxCall(
  url,
  data,
  timeout = defTimeout,
  requestType = 'POST',
  responseType = 'json',
  handleAsRestApi = false
) {
  if (ieVersion < 8) {
    log.error('AJAX/CORS not supported. This browser will not work with Telia ACE.');
    return undefined;
  }

  const beforeUnloadEventName = 'onpagehide' in window ? 'pagehide' : 'beforeunload';

  let ajx;
  if (ieVersion <= 9.9) {
    // IE8 and IE9. Use XDomainRequest
    ajx = new XDomainRequest();
  } else {
    // Modern browsers
    ajx = new XMLHttpRequest();
  }

  const dfr = Q.defer();

  const ajxAbort = (ev) => {
    log.trace('Abort ajax request:', ajx);
    ajx.abort();
    delete ev.returnValue;
    return undefined;
  };

  ajx.onabort = () => {
    log.trace('Ajax onabort event!');
    dfr.reject('aborted');
  };

  if (ieVersion < 9.9) {
    ajx.onload = () => {
      if (ieVersion >= 9) window.removeEventListener(beforeUnloadEventName, ajxAbort);
      if (ajx.responseText) {
        dfr.resolve(JSON.parse(ajx.responseText));
      } else {
        dfr.resolve({});
      }
    };
  } else {
    ajx.onload = () => {
      // If request should be handled as a REST API we should return data even
      // when HTTP status code implies an error.
      if (
        (ajx.readyState === 4 && ajx.status === 200) ||
        (ajx.readyState === 4 && handleAsRestApi)
      ) {
        if (ajx.responseType === 'json') {
          if (ieVersion >= 9) window.removeEventListener(beforeUnloadEventName, ajxAbort);
          dfr.resolve(ajx.response);
        } else {
          if (ieVersion >= 9) window.removeEventListener(beforeUnloadEventName, ajxAbort);
          if (ajx.responseText && responseType === 'json') {
            dfr.resolve(JSON.parse(ajx.responseText));
          } else if (ajx.responseText) {
            dfr.resolve(ajx.responseText);
          } else {
            dfr.resolve({});
          }
        }
      } else {
        log.debug('Ajax load failed. Readystate: ', ajx.readyState, ' Status: ', ajx.status);
        log.trace('ajx: ', ajx);
        dfr.reject('loadfail');
      }
    };
  }

  ajx.onprogress = (event) => {
    log.trace('Ajax progress: ', event);
  };

  ajx.onerror = (event) => {
    log.error('Ajax error, check console message!', event, ajx.statusText);
    if (ieVersion >= 9) {
      window.removeEventListener(beforeUnloadEventName, ajxAbort);
    }
    dfr.reject('error');
  };

  ajx.ontimeout = () => {
    log.trace('Ajax timeout');
    ajx.abort();
    if (ieVersion >= 9) window.removeEventListener(beforeUnloadEventName, ajxAbort);
    dfr.reject('timeout');
  };

  const params = [];
  // REF replace with lodash _.each or similar

  Object.keys(data).forEach((paramName) => {
    if (Object.prototype.hasOwnProperty.call(data, paramName)) {
      params.push(`${paramName}=${encodeURIExtended(data[paramName])}`);
    }
  });
  const postdata = params.join('&');

  ajx.open(requestType, url, true);

  // Ensure this xhr is closed when page unloads.
  // IE8 does not support add/remove Event Listener, skip IE8.
  if (ieVersion >= 9) window.addEventListener(beforeUnloadEventName, ajxAbort);

  ajx.timeout = timeout;
  ajx.responseType = responseType;
  if (ieVersion > 9.9) {
    // Not IE8 and IE9
    ajx.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    ajx.setRequestHeader(
      'Accept',
      responseType === 'json' ? 'application/json' : 'text/*, application/*'
    );
  }

  log.trace('ajax send data to server: ', postdata);
  ajx.send(postdata);

  return dfr.promise;
}
